import { Client } from 'colyseus.js';
import {
  isDuplicateSessionKick,
  isMatchAbortKick,
  isAfkMatchAbortKick,
  isBelotRematchTimeoutKick,
} from '../contracts/wsClose';
import { getColyseusWsUrl } from './colyseusUrl';
import { createCommandEnvelope } from './commandEnvelope';
import {
  SEKA_GAME_STATE_EVENT,
  SEKA_GAME_PRIVATE_HOLE_EVENT,
  SEKA_GAME_CHAT_MESSAGE_EVENT,
  SEKA_GAME_COMMAND,
  SEKA_MATCH_ABORTED_EVENT,
  SEKA_MATCH_WAITING_RECONNECT_EVENT,
  SEKA_MATCH_RECONNECT_RESUMED_EVENT,
  SEKA_MATCH_RECONNECT_TIMEOUT_EVENT,
} from '../contracts/sekaV1';

const EVENT_ERROR = 'system/error';
const EVENT_ACK = 'system/ack';

/** Colyseus: нормальный выход по leave(true) */
const CLOSE_CODE_CONSENTED = 4000;

const SESSION_PREFIX = 'base:game:';

function sessionStorageKey(tableId, gameRoomId) {
  return `${SESSION_PREFIX}${String(tableId)}:${String(gameRoomId || '')}`;
}

function readPersistedReconnect(tableId, gameRoomId) {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(sessionStorageKey(tableId, gameRoomId));
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (o?.reconnectionToken && typeof o.reconnectionToken === 'string') {
      return o.reconnectionToken;
    }
    return null;
  } catch {
    return null;
  }
}

function writePersistedReconnect(tableId, gameRoomId, room) {
  if (typeof sessionStorage === 'undefined' || !room?.reconnectionToken) return;
  try {
    sessionStorage.setItem(
      sessionStorageKey(tableId, gameRoomId),
      JSON.stringify({
        reconnectionToken: room.reconnectionToken,
        at: Date.now(),
      })
    );
  } catch {
    /* quota */
  }
}

function clearPersistedReconnect(tableId, gameRoomId) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(sessionStorageKey(tableId, gameRoomId));
  } catch {
    /* ignore */
  }
}

/**
 * Colyseus `poker` / `belot` (`BaseGameRoom`): `game/state` + reconnect как у секи.
 */
export class ColyseusBaseGameRoomTransport {
  /**
   * @param {import('colyseus.js').Room} room
   * @param {import('colyseus.js').Client} colyseusClient
   * @param {object} joinArgs
   */
  constructor(room, colyseusClient, joinArgs) {
    this.room = room;
    this.client = colyseusClient;
    this.joinArgs = joinArgs;
    this.connected = true;
    this.listeners = new Map();
    this._intentionalDisconnect = false;
    this._reconnectAttempt = 0;
    /** @type {((p: Record<string, unknown>) => void) | null} */
    this._gameStateUserCb = null;
    /** @type {((p: Record<string, unknown>) => void) | null} */
    this._errorUserCb = null;
    /** @type {((p: Record<string, unknown>) => void) | null} */
    this._ackUserCb = null;
    /** @type {((p: Record<string, unknown>) => void) | null} */
    this._privateHoleUserCb = null;
    this._leaveHandler = (code, reason) => {
      void this._handleRoomLeave(code, reason);
    };
    this.room.onLeave(this._leaveHandler);
    this._wire();
    writePersistedReconnect(joinArgs.tableId, joinArgs.gameRoomId, room);
  }

  /**
   * @param {{
   *   tableId: string,
   *   user: { id?: string, username?: string },
   *   accessToken?: string,
   *   gameRoomId?: string,
   *   roomName: 'poker' | 'belot',
   *   skipPersistedReconnect?: boolean,
   * }} args
   */
  static async join(args) {
    const client = new Client(getColyseusWsUrl());
    const token = args.accessToken;
    if (token) {
      client.http.authToken = String(token);
    }

    const joinArgs = {
      tableId: String(args.tableId),
      user: args.user,
      accessToken: token,
      gameRoomId: args.gameRoomId ? String(args.gameRoomId).trim() : '',
      roomName: args.roomName === 'belot' ? 'belot' : 'poker',
    };

    const gid = joinArgs.gameRoomId;
    const roomType = joinArgs.roomName;
    let room = null;

    if (!args.skipPersistedReconnect && gid) {
      const persisted = readPersistedReconnect(joinArgs.tableId, gid);
      if (persisted) {
        try {
          room = await client.reconnect(persisted);
        } catch {
          room = null;
          clearPersistedReconnect(joinArgs.tableId, gid);
        }
      }
    }

    if (!room) {
      const uid = String(args.user?.id ?? '').trim();
      if (!uid) {
        throw new Error('AUTH_USER_REQUIRED');
      }
      const joinOpts = {
        tableId: joinArgs.tableId,
        userId: uid,
        username: String(args.user?.username ?? 'Игрок'),
        ranked: false,
      };
      if (!gid) {
        throw new Error(`GAME_ROOM_ID_REQUIRED:${roomType}`);
      }
      room = await client.joinById(gid, joinOpts);
    }

    return new ColyseusBaseGameRoomTransport(room, client, joinArgs);
  }

  async _handleRoomLeave(code, reason) {
    try {
      this.room.onLeave.remove(this._leaveHandler);
    } catch {
      /* room destroyed */
    }

    const token = this.room?.reconnectionToken;

    if (this._intentionalDisconnect) {
      this.connected = false;
      clearPersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId);
      this._emitLocal('disconnect', { code, reason, intentional: true });
      return;
    }

    if (isDuplicateSessionKick(code, reason)) {
      this.connected = false;
      clearPersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId);
      this._emitLocal('disconnect', { code, reason, duplicateSession: true });
      return;
    }

    if (isMatchAbortKick(code, reason)) {
      this.connected = false;
      clearPersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId);
      this._emitLocal('disconnect', {
        code,
        reason,
        matchAborted: true,
        afkMatchAbort: isAfkMatchAbortKick(code, reason),
      });
      return;
    }

    if (isBelotRematchTimeoutKick(code, reason)) {
      this.connected = false;
      clearPersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId);
      this._emitLocal('disconnect', { code, reason, belotRematchTimeout: true });
      return;
    }

    const maxAttempts = 6;
    if (token && code !== CLOSE_CODE_CONSENTED && this._reconnectAttempt < maxAttempts) {
      this._reconnectAttempt += 1;
      const delayMs = Math.min(4000, 200 * this._reconnectAttempt ** 2);
      await new Promise((r) => setTimeout(r, delayMs));
      try {
        const newRoom = await this.client.reconnect(token);
        this.room = newRoom;
        this.connected = true;
        this._reconnectAttempt = 0;
        newRoom.onLeave(this._leaveHandler);
        this._wire();
        writePersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId, newRoom);
        this._emitLocal('reconnected', { code, reason });
        return;
      } catch (e) {
        console.warn('[base-game] reconnect failed, trying full join', e);
      }
    }

    try {
      const expectedRoomId = String(this.joinArgs?.gameRoomId || '').trim();
      if (!expectedRoomId) {
        throw new Error('GAME_ROOM_ID_REQUIRED_FOR_REJOIN');
      }
      const joinOpts = {
        tableId: this.joinArgs.tableId,
        userId: String(this.joinArgs.user?.id ?? '').trim(),
        username: String(this.joinArgs.user?.username ?? 'Игрок'),
        ranked: false,
      };
      if (!joinOpts.userId) {
        throw new Error('AUTH_USER_REQUIRED');
      }
      const gid = this.joinArgs.gameRoomId ? String(this.joinArgs.gameRoomId).trim() : '';
      const newRoom = await this.client.joinById(gid, joinOpts);
      this.room = newRoom;
      this.connected = true;
      this._reconnectAttempt = 0;
      newRoom.onLeave(this._leaveHandler);
      this._wire();
      writePersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId, newRoom);
      this._emitLocal('reconnected', { code, reason, fallback: 'join' });
    } catch (e) {
      console.error('[base-game] full rejoin failed', e);
      this.connected = false;
      clearPersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId);
      this._emitLocal('disconnect', { code, reason, error: e });
    }
  }

  _wire() {
    this.room.onMessage(SEKA_GAME_STATE_EVENT, (payload) => {
      if (this._gameStateUserCb) this._gameStateUserCb(payload || {});
    });

    this.room.onMessage(SEKA_GAME_PRIVATE_HOLE_EVENT, (payload) => {
      if (this._privateHoleUserCb) this._privateHoleUserCb(payload || {});
    });

    this.room.onMessage(EVENT_ERROR, (payload) => {
      if (this._errorUserCb) this._errorUserCb(payload || { message: 'Ошибка' });
      this._emitLocal('error', payload || {});
    });
    this.room.onMessage(EVENT_ACK, (payload) => {
      if (this._ackUserCb) this._ackUserCb(payload || {});
      this._emitLocal('ack', payload || {});
    });

    this.room.onMessage(SEKA_GAME_CHAT_MESSAGE_EVENT, (payload) => {
      this._emitLocal('game_chat_message', payload || null);
    });

    this.room.onMessage(SEKA_MATCH_ABORTED_EVENT, (payload) => {
      this._emitLocal('match_aborted', payload || {});
    });
    this.room.onMessage(SEKA_MATCH_WAITING_RECONNECT_EVENT, (payload) => {
      this._emitLocal('match_waiting_reconnect', payload || {});
    });
    this.room.onMessage(SEKA_MATCH_RECONNECT_RESUMED_EVENT, (payload) => {
      this._emitLocal('match_reconnect_resumed', payload || {});
    });
    this.room.onMessage(SEKA_MATCH_RECONNECT_TIMEOUT_EVENT, (payload) => {
      this._emitLocal('match_reconnect_timeout', payload || {});
    });
  }

  _emitLocal(eventName, payload) {
    const subs = this.listeners.get(eventName);
    if (!subs) return;
    subs.forEach((cb) => {
      try {
        cb(payload);
      } catch (e) {
        console.error('[base-game]', e);
      }
    });
  }

  on(eventName, cb) {
    if (!this.listeners.has(eventName)) this.listeners.set(eventName, new Set());
    this.listeners.get(eventName).add(cb);
  }

  off(eventName, cb) {
    if (!this.listeners.has(eventName)) return;
    if (!cb) {
      this.listeners.delete(eventName);
      return;
    }
    this.listeners.get(eventName).delete(cb);
  }

  /** @param {(payload: Record<string, unknown>) => void} cb */
  onGameState(cb) {
    this._gameStateUserCb = cb;
  }

  /** @param {(payload: { message?: string } & Record<string, unknown>) => void} cb */
  onError(cb) {
    this._errorUserCb = cb;
  }

  /** @param {(payload: Record<string, unknown>) => void} cb */
  onAck(cb) {
    this._ackUserCb = cb;
  }

  /** @param {(payload: Record<string, unknown>) => void} cb */
  onPrivateHole(cb) {
    this._privateHoleUserCb = cb;
  }

  /**
   * @param {string} command например game/ready, game/start
   * @param {Record<string, unknown>} [data]
   */
  sendGameCommand(command, data = {}) {
    const envelope = createCommandEnvelope(command, data);
    this.room.send('command', envelope);
    return envelope?.meta?.commandId || '';
  }

  /**
   * @param {string} text
   */
  sendGameChat(text) {
    const trimmed = String(text || '').trim();
    if (!trimmed) return;
    this.sendGameCommand(SEKA_GAME_COMMAND.CHAT_SEND, { text: trimmed });
  }

  disconnect() {
    this._intentionalDisconnect = true;
    this.connected = false;
    try {
      this.room.onLeave.remove(this._leaveHandler);
    } catch {
      /* ignore */
    }
    clearPersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId);
    this.room.leave(true);
    /** См. colyseusSekaGameTransport.disconnect — не сбрасываем listeners до обработки MATCH_ABORTED. */
  }
}
